import type { LocationQuery } from 'vue-router'

/**
 * Оформление может быть открыто поверх существующего черновика: `/checkout?draftId=...`.
 *
 * Отличать этот режим важно не косметически: в нём «Сохранить черновик» вызывал бы
 * createDraft() и плодил второй черновик, очищая корзину и уводя пользователя на новый заказ.
 * Обновления черновика на бэкенде пока нет, поэтому кнопка в этом режиме не показывается.
 */
export function resolveResumedDraftId(query: LocationQuery): string | null {
  const draft_id = query['draftId']
  if (typeof draft_id !== 'string') {
    return null
  }

  const trimmed = draft_id.trim()

  return trimmed === '' ? null : trimmed
}

export function isResumingDraft(query: LocationQuery): boolean {
  return resolveResumedDraftId(query) !== null
}

/**
 * Исход попытки подставить позиции черновика в корзину.
 *
 * `replaced` - корзина действительно заменена, дальше checkout работает с черновиком.
 * `rolled_back` - подстановка сорвалась, прежняя корзина возвращена.
 * `inconsistent` - сорвалась и подстановка, и откат.
 * `declined` - пользователь отказался заменять непустую корзину.
 * `unavailable` - черновик не отдался или в нём нет пригодных позиций.
 */
export type DraftResumeOutcome =
  | 'replaced'
  | 'rolled_back'
  | 'inconsistent'
  | 'declined'
  | 'unavailable'

/**
 * Отправлять черновик можно только после `replaced`.
 *
 * Иначе на экране одна корзина, а на бэкенд уйдёт совсем другой состав: `submitDraft`
 * не передаёт позиции, backend берёт их из самого черновика. Наличие `draftId` в URL
 * таким признаком быть не может - при сорвавшемся восстановлении параметр остаётся,
 * а корзина показывается прежняя.
 */
export function isDraftSubmittable(outcome: DraftResumeOutcome | null): boolean {
  return outcome === 'replaced'
}

/** Тот же query без `draftId` - чтобы адрес не обещал режим, из которого мы вышли. */
export function queryWithoutDraftId(query: LocationQuery): LocationQuery {
  const { draftId: _draft_id, ...rest } = query

  return rest
}
